/**
 * Utilities for converting auto-bucketed timeseries points into daily series.
 *
 * - Groups points by UTC day (YYYY-MM-DD derived from `bucket_start`)
 * - Sums numeric values
 * - Fills missing days in the [fromIso, toIso] window with zeros
 */

export type DailyRow<TFields extends Record<string, number>> = {
  date: string; // YYYY-MM-DD
} & TFields;

export function toUtcDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
}

export function toDayKeyUtcFromIso(iso: string): string {
  // Safe for ISO strings with timezone (we always want the UTC date portion).
  // Example: 2026-01-05T12:34:56+00:00 -> 2026-01-05
  const day = iso.split('T')[0];
  return day || iso;
}

export function listUtcDayKeysInclusive(fromIso: string, toIso: string): string[] {
  const from = new Date(fromIso);
  const to = new Date(toIso);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return [];

  const start = toUtcDay(from);
  const end = toUtcDay(to);
  if (start.getTime() > end.getTime()) return [];

  const days: string[] = [];
  const cursor = new Date(start);
  while (cursor.getTime() <= end.getTime()) {
    days.push(cursor.toISOString().split('T')[0]);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

export function sumByUtcDay<TPoint>(
  points: TPoint[],
  getBucketStartIso: (p: TPoint) => string,
  getValue: (p: TPoint) => number
): Record<string, number> {
  const acc: Record<string, number> = {};
  for (const p of points) {
    const iso = getBucketStartIso(p);
    if (!iso) continue;
    const day = toDayKeyUtcFromIso(iso);
    const v = getValue(p);
    if (!Number.isFinite(v)) continue;
    acc[day] = (acc[day] || 0) + v;
  }
  return acc;
}

export function buildDailySeries<TFields extends Record<string, number>>(
  fromIso: string,
  toIso: string,
  fields: Record<keyof TFields, Record<string, number>>,
  zero: TFields
): Array<DailyRow<TFields>> {
  const dayKeys = listUtcDayKeysInclusive(fromIso, toIso);
  return dayKeys.map((date) => {
    const row: DailyRow<TFields> = { date, ...(zero as TFields) };
    (Object.keys(zero) as Array<keyof TFields>).forEach((k) => {
      const map = fields[k] as Record<string, number> | undefined;
      row[k] = map && typeof map[date] === 'number' ? map[date] : zero[k];
    });
    return row;
  });
}


